// src/pages/admin/OrderDetailPage.js
import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { db } from '../firebaseConfig'
import {
  doc,
  getDoc,
  collection,
  getDocs,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore'

// Format timestamp
function formatDate(ts) {
  return ts?.toDate ? ts.toDate().toLocaleString('id-ID') : '-'
}

// Haversine Distance
function calcDistance(lat1, lon1, lat2, lon2) {
  const R = 6371 // km
  const dLat = ((lat2 - lat1) * Math.PI) / 180
  const dLon = ((lon2 - lon1) * Math.PI) / 180
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2)

  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

export default function OrderDetailPage() {
  const { orderId } = useParams()

  const [order, setOrder] = useState(null)
  const [loading, setLoading] = useState(true)

  // Therapist list popup
  const [showPopup, setShowPopup] = useState(false)
  const [therapists, setTherapists] = useState([])
  const [loadingTherapist, setLoadingTherapist] = useState(false)

  // Load Order
  useEffect(() => {
    const load = async () => {
      const ref = doc(db, 'orders', orderId)
      const snap = await getDoc(ref)
      if (snap.exists()) {
        setOrder({ id: snap.id, ...snap.data() })
      }
      setLoading(false)
    }
    load()
  }, [orderId])

  // Load therapists nearest
  const openAssignPopup = async () => {
    if (!order?.lat) return alert('Order tidak punya koordinat.')

    setShowPopup(true)
    setLoadingTherapist(true)

    try {
      const snap = await getDocs(collection(db, 'therapists'))

      const list = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((t) => t.online && t.isAvailable && t.lat && t.lng)
        .map((t) => ({
          ...t,
          distance: calcDistance(order.lat, order.lng, t.lat, t.lng),
        }))
        .sort((a, b) => a.distance - b.distance)

      setTherapists(list)
    } catch (err) {
      console.error(err)
      alert('Gagal memuat terapis.')
    }

    setLoadingTherapist(false)
  }

  // Assign therapist safely
  const assignTherapist = async (tid) => {
    const ok = window.confirm('Assign therapist ini ke order?')
    if (!ok) return

    try {
      const ref = doc(db, 'orders', order.id)

      await updateDoc(ref, {
        assignedTherapistId: tid,
        status: 'accepted',
        acceptedAt: serverTimestamp(),
      })

      alert('Berhasil assign therapist.')
      window.location.reload()
    } catch (err) {
      console.error(err)
      alert('Gagal assign.')
    }
  }

  // ============================
  // 🔥 FUNGSI BARU (ADMIN CONTROL)
  // ============================

  // Force complete order
  const forceComplete = async () => {
    if (!window.confirm('Yakin ingin menyelesaikan order ini sekarang?')) return

    try {
      const ref = doc(db, 'orders', order.id)
      await updateDoc(ref, {
        status: 'completed',
        completedAt: serverTimestamp(),
        adminForcedComplete: true,
      })

      alert('Order diselesaikan secara paksa.')
      window.location.reload()
    } catch (err) {
      console.error(err)
      alert('Gagal menyelesaikan order.')
    }
  }

  // Cancel order
  const cancelOrder = async () => {
    if (!window.confirm('Yakin ingin membatalkan order ini?')) return

    try {
      const ref = doc(db, 'orders', order.id)
      await updateDoc(ref, {
        status: 'cancelled',
        cancelledAt: serverTimestamp(),
        adminCancelled: true,
      })

      alert('Order telah dibatalkan.')
      window.location.reload()
    } catch (err) {
      console.error(err)
      alert('Gagal membatalkan order.')
    }
  }

  // Remove assigned therapist
  const removeTherapist = async () => {
    if (!window.confirm('Hapus terapis dari order ini?')) return

    try {
      const ref = doc(db, 'orders', order.id)
      await updateDoc(ref, {
        assignedTherapistId: null,
        status: 'waiting',
        unassignedAt: serverTimestamp(),
        adminUnassigned: true,
      })

      alert('Terapis dilepas dari order.')
      window.location.reload()
    } catch (err) {
      console.error(err)
      alert('Gagal menghapus terapis.')
    }
  }

  // ============================

  if (loading)
    return React.createElement(
      'div',
      { className: 'p-5 text-white' },
      'Loading...'
    )

  if (!order)
    return React.createElement(
      'div',
      { className: 'p-5 text-white' },
      'Order tidak ditemukan'
    )

  // UI
  return React.createElement('div', { className: 'p-5 text-white space-y-5' }, [
    // Title
    React.createElement(
      'h1',
      { key: 'title', className: 'text-2xl font-bold' },
      `Detail Order — ${order.orderId}`
    ),

    // Card Detail
    React.createElement(
      'div',
      {
        key: 'card',
        className: 'p-4 bg-gray-800 rounded border border-gray-700 space-y-1',
      },
      [
        React.createElement('p', { key: 'u' }, `User: ${order.userName}`),
        React.createElement('p', { key: 's' }, `Service: ${order.serviceName}`),
        React.createElement(
          'p',
          { key: 'd' },
          `Duration: ${order.duration} menit`
        ),
        React.createElement('p', { key: 'st' }, `Status: ${order.status}`),
        React.createElement('p', { key: 'g' }, `Gender: ${order.userGender}`),
        React.createElement('p', { key: 'a' }, `Alamat: ${order.address}`),
        React.createElement(
          'p',
          { key: 'c' },
          `Created: ${formatDate(order.createdAt)}`
        ),
        React.createElement(
          'p',
          { key: 'ac' },
          `Accepted: ${formatDate(order.acceptedAt)}`
        ),
        React.createElement(
          'p',
          { key: 'st2' },
          `Started: ${formatDate(order.startedAt)}`
        ),
      ]
    ),

    // MAP
    React.createElement('iframe', {
      key: 'map',
      width: '100%',
      height: '300',
      className: 'rounded border border-gray-700',
      loading: 'lazy',
      allowFullScreen: true,
      referrerPolicy: 'no-referrer-when-downgrade',
      src:
        `https://www.google.com/maps/embed/v1/view?key=AIzaSyAbCtGg-4eW2h07-rop8N7ka0ai1R-VFfQ` +
        `&center=${order.lat},${order.lng}&zoom=17`,
    }),

    // Buttons
    React.createElement(
      'div',
      { key: 'btns', className: 'flex gap-3 flex-wrap' },
      [
        // Lihat chat
        React.createElement(
          'button',
          {
            key: 'chat',
            className: 'px-4 py-2 bg-indigo-600 rounded hover:bg-indigo-700',
            onClick: () =>
              (window.location.href = `/orders/${order.orderId}/chat`),
          },
          'Lihat Chat'
        ),

        // Assign therapist (Hanya jika status waiting atau accepted)
        (order.status === 'waiting' || order.status === 'pending') &&
          React.createElement(
            'button',
            {
              key: 'assign',
              className: 'px-4 py-2 bg-green-600 rounded hover:bg-green-700',
              onClick: openAssignPopup,
            },
            'Assign Therapist Terdekat'
          ),

        // Force complete (Hanya jika belum completed/cancelled)
        order.status !== 'completed' &&
          order.status !== 'cancelled' &&
          React.createElement(
            'button',
            {
              key: 'force-complete',
              className: 'px-4 py-2 bg-green-700 rounded hover:bg-green-800',
              onClick: forceComplete,
            },
            'Selesaikan Sekarang'
          ),

        // Cancel order (JANGAN TAMPILKAN JIKA STATUS COMPLETED ATAU CANCELLED)
        order.status !== 'completed' &&
          order.status !== 'cancelled' &&
          React.createElement(
            'button',
            {
              key: 'cancel-order',
              className: 'px-4 py-2 bg-red-700 rounded hover:bg-red-800',
              onClick: cancelOrder,
            },
            'Batalkan Order'
          ),

        // Remove therapist — only if assigned AND not completed
        order.assignedTherapistId &&
          order.status !== 'completed' &&
          order.status !== 'cancelled' &&
          React.createElement(
            'button',
            {
              key: 'remove-therapist',
              className: 'px-4 py-2 bg-yellow-600 rounded hover:bg-yellow-700',
              onClick: removeTherapist,
            },
            'Hapus Terapis'
          ),
      ]
    ),

    // POPUP
    showPopup &&
      React.createElement(
        'div',
        {
          key: 'popup',
          className:
            'fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center',
        },
        [
          React.createElement(
            'div',
            {
              key: 'box',
              className:
                'bg-gray-900 p-5 rounded-lg border border-gray-700 w-[400px] max-h-[80vh] overflow-y-auto',
            },
            [
              React.createElement(
                'h2',
                { key: 'title2', className: 'text-xl font-bold mb-3' },
                'Pilih Terapis Terdekat'
              ),

              loadingTherapist
                ? React.createElement(
                    'p',
                    { key: 'loading', className: 'text-gray-300' },
                    'Loading...'
                  )
                : therapists.length === 0
                ? React.createElement(
                    'p',
                    { key: 'no', className: 'text-gray-400' },
                    'Tidak ada terapis aktif & tersedia.'
                  )
                : therapists.map((t) =>
                    React.createElement(
                      'div',
                      {
                        key: t.id,
                        className:
                          'p-3 bg-gray-800 rounded mb-3 border border-gray-700',
                      },
                      [
                        React.createElement(
                          'p',
                          { key: 'n', className: 'text-white font-bold' },
                          t.name
                        ),
                        React.createElement(
                          'p',
                          { key: 'd', className: 'text-gray-300 text-sm' },
                          `Jarak: ${t.distance.toFixed(2)} km`
                        ),
                        React.createElement(
                          'button',
                          {
                            key: 'btn',
                            className:
                              'mt-2 px-3 py-1 bg-blue-600 rounded hover:bg-blue-700',
                            onClick: () => assignTherapist(t.id),
                          },
                          'Assign'
                        ),
                      ]
                    )
                  ),

              React.createElement(
                'button',
                {
                  key: 'close',
                  className:
                    'mt-4 w-full px-4 py-2 bg-red-600 rounded hover:bg-red-700',
                  onClick: () => setShowPopup(false),
                },
                'Tutup'
              ),
            ]
          ),
        ]
      ),
  ])
}